import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import ContactFooter from "./ContactFooter";
import getLocalizedValue from "../utils/getLocalizedValue";
import "../style/liquidiq.css";

const assetRoot = `${process.env.PUBLIC_URL || ""}/images/liquidiq`;

const focusStates = {
  full: { origin: "50% 50%", scale: 1.04 },
  fans: { origin: "23% 51%", scale: 1.82 },
  airflow: { origin: "61% 11%", scale: 2.05 },
  cpu: { origin: "39% 73%", scale: 1.95 },
  gpu: { origin: "55% 73%", scale: 1.95 },
};

const clamp = (value, min = 0, max = 1) =>
  Math.min(max, Math.max(min, value));

const smoothstep = (value) => value * value * (3 - 2 * value);
const smootherstep = (value) =>
  value * value * value * (value * (value * 6 - 15) + 10);

function FocusControl({
  id,
  label,
  description,
  selected,
  interactive,
  onSelect,
}) {
  return (
    <button
      type="button"
      className={`liquidiq-focus-control liquidiq-focus-${id}`}
      aria-pressed={selected}
      tabIndex={interactive ? 0 : -1}
      onClick={() => onSelect(id)}
    >
      <span className="liquidiq-focus-label">{label}</span>
      {description ? (
        <span
          className="liquidiq-focus-description"
          aria-hidden={!selected}
        >
          {description}
        </span>
      ) : null}
    </button>
  );
}

function LiquidIQPage() {
  const reduceMotion = useReducedMotion();
  const [selectedPart, setSelectedPart] = useState("full");
  const [closerInteractive, setCloserInteractive] = useState(false);

  const pageRef = useRef(null);
  const assemblyRef = useRef(null);
  const assemblyStageRef = useRef(null);
  const assemblyCopyRef = useRef(null);
  const exteriorRef = useRef(null);
  const cutawayRef = useRef(null);
  const closerRef = useRef(null);
  const intelligenceRef = useRef(null);
  const intelligenceCopyRef = useRef(null);
  const phoneLeftRef = useRef(null);
  const phoneRightRef = useRef(null);
  const directRef = useRef(null);
  const directStageRef = useRef(null);
  const directCopyRef = useRef(null);
  const directOutcomeRef = useRef(null);
  const thermalHotRef = useRef(null);
  const thermalCoolRef = useRef(null);
  const thermalScanRef = useRef(null);
  const focusLineRef = useRef(null);
  const pumpingProofRef = useRef(null);
  const directStatsRef = useRef(null);
  const selectedPartRef = useRef(selectedPart);
  const selectionScrollTopRef = useRef(null);
  const closerInteractiveRef = useRef(false);
  const updateStoryRef = useRef(null);

  const parts = [
    {
      id: "fans",
      label: getLocalizedValue("Fans", "팬"),
      description: getLocalizedValue(
        "Draw cool air through the radiator.",
        "라디에이터를 통해 시원한 공기를 끌어옵니다."
      ),
    },
    {
      id: "airflow",
      label: getLocalizedValue("Aerodynamic airflow", "공기 흐름 설계"),
      description: getLocalizedValue(
        "The K shaped opening guides air through the radiator and keeps warm air from circling back.",
        "K 형상의 개구부가 공기를 라디에이터로 유도하고 따뜻한 공기가 다시 유입되는 것을 줄입니다."
      ),
    },
    {
      id: "cpu",
      label: getLocalizedValue("CPU cooling", "CPU 냉각"),
      description: getLocalizedValue(
        "IMMC2 moves heat from the processor straight into the liquid loop.",
        "IMMC2가 프로세서의 열을 곧바로 액체 냉각 루프로 전달합니다."
      ),
    },
    {
      id: "gpu",
      label: getLocalizedValue("GPU cooling", "GPU 냉각"),
      description: getLocalizedValue(
        "Liquid cooling carries heat away from the graphics hardware during demanding work.",
        "고부하 작업 중 그래픽 하드웨어의 열을 액체 냉각으로 빠르게 옮깁니다."
      ),
    },
    {
      id: "full",
      label: getLocalizedValue("Full view", "전체 보기"),
      description: "",
    },
  ];

  useEffect(() => {
    selectedPartRef.current = selectedPart;
    updateStoryRef.current?.();
  }, [selectedPart]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "LiquidIQ | KoolMicro";

    const page = pageRef.current;
    const assembly = assemblyRef.current;
    const assemblyStage = assemblyStageRef.current;
    const direct = directRef.current;
    const directStage = directStageRef.current;

    if (!page || !assembly || !assemblyStage || !direct || !directStage) {
      document.title = previousTitle;
      return undefined;
    }

    let frame = 0;
    let renderedPhoneProgress = null;
    let lastPhoneFrame = performance.now();

    const updateStory = () => {
      frame = 0;
      const scrollTop = page.scrollTop;

      if (reduceMotion) {
        const state = focusStates[selectedPartRef.current] || focusStates.full;
        closerInteractiveRef.current = true;
        setCloserInteractive(true);
        exteriorRef.current.style.opacity = "0";
        cutawayRef.current.style.opacity = "1";
        cutawayRef.current.style.transition = "none";
        cutawayRef.current.style.transformOrigin = state.origin;
        cutawayRef.current.style.transform = `translateX(-50%) translateY(-22px) scale(${state.scale})`;
        assemblyCopyRef.current.style.opacity = "0";
        closerRef.current.style.opacity = "1";
        closerRef.current.style.pointerEvents = "auto";
        phoneLeftRef.current.style.transform =
          "translate3d(calc(-100% - 18px), 0, 0) rotateY(0deg) rotateZ(0deg)";
        phoneRightRef.current.style.transform =
          "translate3d(18px, 0, 0) rotateY(0deg) rotateZ(0deg)";
        phoneLeftRef.current.style.opacity = "1";
        phoneRightRef.current.style.opacity = "1";
        intelligenceCopyRef.current.style.opacity = "1";
        intelligenceCopyRef.current.style.transform = "translateY(0)";
        thermalHotRef.current.style.opacity = "0";
        thermalCoolRef.current.style.opacity = "0.9";
        thermalCoolRef.current.style.transform = "scale(1.1)";
        thermalScanRef.current.style.opacity = "0";
        directCopyRef.current.style.opacity = "1";
        directCopyRef.current.style.transform = "translateY(0)";
        directOutcomeRef.current.style.opacity = "1";
        directOutcomeRef.current.style.transform = "translateY(0)";
        pumpingProofRef.current.style.opacity = "0";
        pumpingProofRef.current.style.transform = "translateY(0)";
        directStatsRef.current.style.opacity = "1";
        directStatsRef.current.style.transform = "translateY(0)";
        focusLineRef.current.style.opacity = "1";
        focusLineRef.current.style.strokeDashoffset = "0";
        directStage.querySelectorAll(".liquidiq-cooling-flow").forEach((path) => {
          path.style.strokeDashoffset = "0";
        });
        return;
      }

      const assemblyStart = assembly.offsetTop - 95;
      const assemblyDistance = Math.max(
        1,
        assembly.offsetHeight - assemblyStage.offsetHeight
      );
      const assemblyProgress = clamp(
        (scrollTop - assemblyStart) / assemblyDistance
      );
      const assemblyMotion = smoothstep(assemblyProgress);
      const cutawayReveal = smoothstep(
        clamp((assemblyProgress - 0.16) / 0.5)
      );
      const closerReveal = smoothstep(
        clamp((assemblyProgress - 0.6) / 0.2)
      );
      const handoffProgress = smoothstep(
        clamp((assemblyProgress - 0.6) / 0.2)
      );
      const assemblyScale = 0.82 + assemblyMotion * 0.22;
      const assemblyRise = 26 - assemblyMotion * 48;
      const assemblyTransform = `translateX(-50%) translateY(${assemblyRise}px) scale(${assemblyScale})`;
      const handoffScale =
        assemblyScale +
        (focusStates.full.scale - assemblyScale) * handoffProgress;
      const handoffRise = assemblyRise + (-22 - assemblyRise) * handoffProgress;

      exteriorRef.current.style.transform = assemblyTransform;
      exteriorRef.current.style.opacity = String(1 - cutawayReveal);
      cutawayRef.current.style.opacity = String(cutawayReveal);

      if (assemblyProgress >= 0.8) {
        cutawayRef.current.style.transition =
          "transform 650ms cubic-bezier(0.16, 1, 0.3, 1), transform-origin 650ms cubic-bezier(0.16, 1, 0.3, 1)";
        const state = focusStates[selectedPartRef.current] || focusStates.full;
        cutawayRef.current.style.transformOrigin = state.origin;
        cutawayRef.current.style.transform = `translateX(-50%) translateY(-22px) scale(${state.scale})`;
      } else {
        cutawayRef.current.style.transition = "none";
        cutawayRef.current.style.transformOrigin = "50% 50%";
        cutawayRef.current.style.transform = `translateX(-50%) translateY(${handoffRise}px) scale(${handoffScale})`;
      }

      assemblyCopyRef.current.style.opacity = String(
        1 - clamp((assemblyProgress - 0.48) / 0.16)
      );
      closerRef.current.style.opacity = String(closerReveal);
      closerRef.current.style.pointerEvents =
        assemblyProgress >= 0.8 ? "auto" : "none";
      const isCloserInteractive = assemblyProgress >= 0.8;
      if (closerInteractiveRef.current !== isCloserInteractive) {
        closerInteractiveRef.current = isCloserInteractive;
        if (
          !isCloserInteractive &&
          closerRef.current.contains(document.activeElement)
        ) {
          document.activeElement.blur();
        }
        setCloserInteractive(isCloserInteractive);
      }

      const intelligence = intelligenceRef.current;
      const phoneDistance = Math.max(
        1,
        intelligence.offsetHeight - page.clientHeight * 0.6
      );
      const phoneProgress = clamp(
        (scrollTop - intelligence.offsetTop + page.clientHeight * 0.4) /
          phoneDistance
      );

      if (renderedPhoneProgress === null) {
        renderedPhoneProgress = phoneProgress;
      } else {
        const now = performance.now();
        const deltaTime = Math.min(32, Math.max(0, now - lastPhoneFrame));
        const damping = 1 - Math.exp(-deltaTime / 72);
        lastPhoneFrame = now;
        renderedPhoneProgress +=
          (phoneProgress - renderedPhoneProgress) * damping;
      }

      const phoneDelta = Math.abs(phoneProgress - renderedPhoneProgress);
      if (phoneDelta < 0.0005) renderedPhoneProgress = phoneProgress;

      const phoneMotion = smootherstep(
        clamp((renderedPhoneProgress - 0.1) / 0.84)
      );
      const intelligenceCopyExit = smoothstep(
        clamp((renderedPhoneProgress - 0.74) / 0.18)
      );
      const swivel = -28 + phoneMotion * 56;
      const lift = 56 - phoneMotion * 112;

      intelligenceCopyRef.current.style.opacity = String(
        1 - intelligenceCopyExit
      );
      intelligenceCopyRef.current.style.transform = `translateY(${-28 * intelligenceCopyExit}px)`;
      phoneLeftRef.current.style.transform = `translate3d(calc(-100% - 18px), ${lift}px, 0) rotateY(${swivel}deg) rotateZ(${-2 + phoneMotion * 2}deg)`;
      phoneRightRef.current.style.transform = `translate3d(18px, ${-lift}px, 0) rotateY(${-swivel}deg) rotateZ(${2 - phoneMotion * 2}deg)`;
      phoneLeftRef.current.style.opacity = "1";
      phoneRightRef.current.style.opacity = "1";

      const directDistance = Math.max(
        1,
        direct.offsetHeight - directStage.offsetHeight
      );
      const directProgress = clamp(
        (scrollTop - direct.offsetTop + 95) / directDistance
      );
      const flowProgress = smoothstep(
        clamp((directProgress - 0.16) / 0.52)
      );
      const outcomeProgress = smoothstep(
        clamp((directProgress - 0.7) / 0.16)
      );
      const statsProgress = smoothstep(
        clamp((directProgress - 0.7) / 0.12)
      );
      const proofExitProgress = smoothstep(
        clamp((directProgress - 0.52) / 0.18)
      );
      const scanProgress = smoothstep(
        clamp((directProgress - 0.12) / 0.68)
      );
      const scanVisible =
        clamp(scanProgress * 8) *
        (1 - clamp((scanProgress - 0.9) / 0.1));

      directStage.querySelectorAll(".liquidiq-cooling-flow").forEach((path) => {
        path.style.strokeDashoffset = String(260 * (1 - flowProgress));
      });
      thermalHotRef.current.style.opacity = String(0.78 * (1 - scanProgress));
      thermalCoolRef.current.style.opacity = String(0.9 * scanProgress);
      thermalCoolRef.current.style.transform = `scale(${0.72 + scanProgress * 0.38})`;
      thermalScanRef.current.style.left = `${18 + scanProgress * 64}%`;
      thermalScanRef.current.style.opacity = String(scanVisible);
      focusLineRef.current.style.opacity = String(outcomeProgress);
      focusLineRef.current.style.strokeDashoffset = String(
        360 * (1 - outcomeProgress)
      );
      directCopyRef.current.style.opacity = String(
        1 - clamp((directProgress - 0.44) / 0.22)
      );
      directCopyRef.current.style.transform = `translateY(${-38 * clamp((directProgress - 0.38) / 0.28)}px)`;
      directOutcomeRef.current.style.opacity = String(outcomeProgress);
      directOutcomeRef.current.style.transform = `translateY(${26 * (1 - outcomeProgress)}px)`;
      pumpingProofRef.current.style.opacity = String(1 - proofExitProgress);
      pumpingProofRef.current.style.transform = `translateY(${-18 * proofExitProgress}px)`;
      directStatsRef.current.style.opacity = String(statsProgress);
      directStatsRef.current.style.transform = `translateY(${20 * (1 - statsProgress)}px)`;

      if (phoneDelta >= 0.0005 && !frame) {
        frame = window.requestAnimationFrame(updateStory);
      }
    };

    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateStory);
    };

    const handlePageScroll = () => {
      const selectionScrollTop = selectionScrollTopRef.current;
      const movedSinceSelection =
        selectionScrollTop === null ||
        Math.abs(page.scrollTop - selectionScrollTop) > 16;

      if (selectedPartRef.current !== "full" && movedSinceSelection) {
        selectedPartRef.current = "full";
        selectionScrollTopRef.current = null;
        setSelectedPart("full");
      }
      scheduleUpdate();
    };

    updateStoryRef.current = updateStory;
    page.addEventListener("scroll", handlePageScroll, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    updateStory();

    return () => {
      document.title = previousTitle;
      page.removeEventListener("scroll", handlePageScroll);
      window.removeEventListener("resize", scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
      updateStoryRef.current = null;
    };
  }, [reduceMotion]);

  const selectedLabel =
    parts.find((part) => part.id === selectedPart)?.label || parts[4].label;

  const handlePartSelect = (partId) => {
    selectionScrollTopRef.current = pageRef.current?.scrollTop ?? null;
    selectedPartRef.current = partId;
    setSelectedPart(partId);
  };

  return (
    <main className="liquidiq-page" ref={pageRef}>
      <section className="liquidiq-hero" aria-labelledby="liquidiq-title">
        <div className="liquidiq-hero-copy">
          <p className="liquidiq-eyebrow">LiquidIQ</p>
          <h1 id="liquidiq-title">
            {getLocalizedValue("Cooling, reimagined.", "냉각의 새로운 기준.")}
          </h1>
          <p className="liquidiq-hero-description">
            {getLocalizedValue(
              "Energy Efficient Liquid Cooling Compute Platform",
              "에너지 효율적인 액체 냉각 컴퓨팅 플랫폼"
            )}
          </p>
          <p className="liquidiq-release">
            {getLocalizedValue(
              "Releasing February 28th 2027",
              "2027년 2월 28일 출시 예정"
            )}
          </p>
        </div>
        <div className="liquidiq-hero-media">
          <img
            src={`${assetRoot}/angle-45-hq.png`}
            fetchpriority="high"
            decoding="async"
            alt={getLocalizedValue(
              "LiquidIQ workstation shown from a 45 degree angle",
              "45도 각도에서 본 LiquidIQ 워크스테이션"
            )}
          />
        </div>
      </section>

      <section className="liquidiq-assembly" ref={assemblyRef}>
        <div className="liquidiq-assembly-stage" ref={assemblyStageRef}>
          <div className="liquidiq-assembly-copy" ref={assemblyCopyRef}>
            <p className="liquidiq-eyebrow">
              {getLocalizedValue("LiquidIQ assembly", "LiquidIQ 구조")}
            </p>
            <h2>
              {getLocalizedValue(
                "Built from the inside out.",
                "내부에서부터 완성했습니다."
              )}
            </h2>
            <p className="liquidiq-scroll-label">
              {getLocalizedValue("Scroll to explore", "스크롤하여 살펴보기")}
            </p>
          </div>

          <div
            className="liquidiq-closer"
            ref={closerRef}
            aria-hidden={!closerInteractive}
          >
            <h2>{getLocalizedValue("Take a closer look.", "더 가까이 살펴보세요.")}</h2>
            <div
              className="liquidiq-focus-controls"
              aria-label={getLocalizedValue(
                "LiquidIQ component focus controls",
                "LiquidIQ 구성 요소 보기"
              )}
            >
              {parts.map((part) => (
                <FocusControl
                  key={part.id}
                  {...part}
                  selected={selectedPart === part.id}
                  interactive={closerInteractive}
                  onSelect={handlePartSelect}
                />
              ))}
            </div>
          </div>

          <div
            ref={exteriorRef}
            className="liquidiq-assembly-product liquidiq-exterior"
          >
            <img
              src={`${assetRoot}/front-hq.png`}
              loading="lazy"
              decoding="async"
              alt={getLocalizedValue(
                "Complete front view of LiquidIQ",
                "LiquidIQ 전체 정면"
              )}
            />
          </div>
          <div
            ref={cutawayRef}
            className="liquidiq-assembly-product liquidiq-cutaway"
          >
            <img
              src={`${assetRoot}/front-cutaway-hq.png`}
              loading="lazy"
              decoding="async"
              alt={getLocalizedValue(
                `LiquidIQ cutaway focused on ${selectedLabel}`,
                `${selectedLabel}에 초점을 맞춘 LiquidIQ 내부 모습`
              )}
            />
          </div>
        </div>
      </section>

      <section className="liquidiq-intelligence" ref={intelligenceRef}>
        <div className="liquidiq-intelligence-copy" ref={intelligenceCopyRef}>
          <h2>
            {getLocalizedValue(
              "Intelligence that adapts.",
              "상황에 맞춰 반응하는 지능."
            )}
          </h2>
          <p>
            {getLocalizedValue(
              "Before the system is built, modeling tunes the microchannels for its chip and working environment. Once it is running, LiquidIQ reads temperature and flow, then responds as conditions change.",
              "시스템을 만들기 전에 모델링으로 칩과 사용 환경에 맞는 마이크로채널을 설계합니다. 작동 중에는 LiquidIQ가 온도와 유량을 읽고 상태 변화에 맞춰 반응합니다."
            )}
          </p>
        </div>
        <div className="liquidiq-phone-stage">
          <img
            ref={phoneLeftRef}
            className="liquidiq-phone liquidiq-phone-left"
            src={`${assetRoot}/phone-performance-hq.png`}
            loading="lazy"
            decoding="async"
            alt={getLocalizedValue(
              "LiquidIQ performance screen",
              "LiquidIQ 성능 화면"
            )}
          />
          <img
            ref={phoneRightRef}
            className="liquidiq-phone liquidiq-phone-right"
            src={`${assetRoot}/phone-thermal-hq.png`}
            loading="lazy"
            decoding="async"
            alt={getLocalizedValue(
              "LiquidIQ thermal map screen",
              "LiquidIQ 열 지도 화면"
            )}
          />
        </div>
      </section>

      <div className="liquidiq-intelligence-note-band">
        <motion.p
          initial={reduceMotion ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.65, root: pageRef }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          {getLocalizedValue(
            "Energy use and estimated savings stay visible in real time.",
            "에너지 사용량과 예상 절감량을 실시간으로 확인할 수 있습니다."
          )}
        </motion.p>
      </div>

      <section className="liquidiq-direct" ref={directRef}>
        <div className="liquidiq-direct-stage" ref={directStageRef}>
          <img
            className="liquidiq-direct-product"
            src={`${assetRoot}/front-cutaway-hq.png`}
            loading="lazy"
            decoding="async"
            alt={getLocalizedValue(
              "LiquidIQ direct cooling system",
              "LiquidIQ 직접 냉각 시스템"
            )}
          />

          <div className="liquidiq-thermal-field" aria-hidden="true">
            <span className="liquidiq-thermal-hot" ref={thermalHotRef} />
            <span className="liquidiq-thermal-cool" ref={thermalCoolRef} />
          </div>
          <div
            className="liquidiq-thermal-scan"
            ref={thermalScanRef}
            aria-hidden="true"
          />

          <svg
            className="liquidiq-cooling-overlay"
            viewBox="0 0 900 900"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-labelledby="liquidiq-cooling-title liquidiq-cooling-description"
          >
            <title id="liquidiq-cooling-title">
              {getLocalizedValue(
                "Coolant path through LiquidIQ",
                "LiquidIQ 내부 냉각수 경로"
              )}
            </title>
            <desc id="liquidiq-cooling-description">
              {getLocalizedValue(
                "Two coolant paths move from the reservoir toward the processor cold plate.",
                "두 개의 냉각수 경로가 리저버에서 프로세서 콜드 플레이트로 이어집니다."
              )}
            </desc>
            <path
              className="liquidiq-cooling-track"
              d="M444 400 L444 568 C444 588 447 606 444 622"
            />
            <path
              className="liquidiq-cooling-track"
              d="M473 400 L473 568 C473 588 476 606 473 622"
            />
            <path
              className="liquidiq-cooling-flow"
              d="M444 400 L444 568 C444 588 447 606 444 622"
            />
            <path
              className="liquidiq-cooling-flow"
              d="M473 400 L473 568 C473 588 476 606 473 622"
            />
            <path
              ref={focusLineRef}
              className="liquidiq-cooling-focus-line"
              d="M459 628 L626 628 L710 578"
            />
          </svg>

          <div className="liquidiq-direct-copy" ref={directCopyRef}>
            <p className="liquidiq-eyebrow">IMMC2</p>
            <h2>
              {getLocalizedValue(
                "Direct cooling. Precisely controlled.",
                "칩에서 바로 식히고 정밀하게 제어합니다."
              )}
            </h2>
            <p>
              {getLocalizedValue(
                "Coolant meets the processor at the source, then carries heat away through a controlled loop.",
                "냉각수가 프로세서에서 열을 받아 제어된 루프를 따라 곧바로 옮깁니다."
              )}
            </p>
          </div>

          <div className="liquidiq-pumping-proof" ref={pumpingProofRef}>
            <span>{getLocalizedValue("Lower pumping demand", "더 낮은 펌핑 동력")}</span>
            <strong>{getLocalizedValue("Up to 90%", "최대 90%")}</strong>
            <p>{getLocalizedValue("less pumping power", "낮은 펌핑 동력")}</p>
            <small>
              {getLocalizedValue(
                "Compared with conventional cooling approaches.",
                "기존 냉각 방식과 비교한 수치입니다."
              )}
            </small>
          </div>

          <div className="liquidiq-direct-results">
            <div className="liquidiq-thermal-stats" ref={directStatsRef}>
              <p>
                {getLocalizedValue(
                  "KoolMicro testing data",
                  "KoolMicro 시험 데이터"
                )}
              </p>
              <div className="liquidiq-thermal-stats-grid">
                <div>
                  <strong>{getLocalizedValue("40%", "40%")}</strong>
                  <span>
                    {getLocalizedValue(
                      "lower thermal resistance",
                      "낮은 열 저항"
                    )}
                  </span>
                </div>
                <div>
                  <strong>{getLocalizedValue("10 kW", "10 kW")}</strong>
                  <span>
                    {getLocalizedValue(
                      "removed per cold plate",
                      "콜드 플레이트당 열 제거"
                    )}
                  </span>
                </div>
                <div>
                  <strong>
                    {getLocalizedValue("50% lower", "50% 감소")}
                  </strong>
                  <span>
                    {getLocalizedValue(
                      "energy use and associated carbon emissions",
                      "에너지 사용 및 관련 탄소 배출"
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="liquidiq-direct-outcome" ref={directOutcomeRef}>
              <strong>{getLocalizedValue("Heat leaves here.", "열은 여기서 빠져나갑니다.")}</strong>
              <span>
                {getLocalizedValue(
                  "The cold plate sits directly on the processor.",
                  "콜드 플레이트가 프로세서에 직접 닿습니다."
                )}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="liquidiq-efficiency">
        <motion.div
          className="liquidiq-efficiency-inner"
          initial={reduceMotion ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.24, root: pageRef }}
          transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <div className="liquidiq-efficiency-heading">
            <h2>
              {getLocalizedValue(
                "Only the cooling the work requires.",
                "필요한 만큼만 냉각합니다."
              )}
            </h2>
          </div>
          <p className="liquidiq-efficiency-copy">
            {getLocalizedValue(
              "LiquidIQ reads temperature, flow, pressure, and power in real time. It adjusts coolant flow, inlet temperature, and fan speed to match the work.",
              "LiquidIQ는 온도, 유량, 압력, 전력을 실시간으로 읽습니다. 냉각수 유량과 입구 온도, 팬 속도를 작업에 맞춰 조정합니다."
            )}
          </p>
          <svg
            className="liquidiq-response-line"
            viewBox="0 0 1280 110"
            aria-hidden="true"
          >
            <motion.path
              d="M0 55H200L246 15L292 95L346 31L400 55H640L684 5L732 107L782 33L838 55H1280"
              fill="none"
              stroke="#1769ff"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reduceMotion ? false : { pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, amount: 0.55, root: pageRef }}
              transition={{
                duration: reduceMotion ? 0 : 1.15,
                ease: [0.22, 1, 0.36, 1],
              }}
            />
            <circle cx="246" cy="15" r="7" fill="#1769ff" />
            <circle cx="732" cy="107" r="7" fill="#20b58b" />
          </svg>
          <h3>
            {getLocalizedValue(
              "Fewer fans. Quieter operation. Less material waste.",
              "더 적은 팬. 더 조용한 작동. 더 적은 소재 낭비."
            )}
          </h3>
          <p className="liquidiq-efficiency-detail">
            {getLocalizedValue(
              "Direct chip cooling lowers fan demand and cooling overhead. Shared radii let the aluminum front pieces use sheet material more efficiently.",
              "칩 직접 냉각은 팬 수요와 냉각 부담을 낮춥니다. 동일한 곡률을 공유하는 알루미늄 전면 부품은 판재를 더 효율적으로 사용합니다."
            )}
          </p>
        </motion.div>
      </section>

      <section className="liquidiq-future">
        <motion.img
          src={`${assetRoot}/laboratory-hq.png`}
          loading="lazy"
          decoding="async"
          alt={getLocalizedValue(
            "LiquidIQ workstation in a semiconductor laboratory",
            "반도체 연구실의 LiquidIQ 워크스테이션"
          )}
          initial={reduceMotion ? false : { scale: 1.045 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, amount: 0.3, root: pageRef }}
          transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
        />
        <div className="liquidiq-future-shade" aria-hidden="true" />
        <h2>
          <span className="liquidiq-future-built">
            {getLocalizedValue("Built for", "다가올 시대를")}
          </span>
          <span className="liquidiq-future-what">
            {getLocalizedValue("what", "위해")}
          </span>
          <span className="liquidiq-future-next">
            {getLocalizedValue("comes next", "만들었습니다")}
          </span>
        </h2>
      </section>

      <ContactFooter />
    </main>
  );
}

export default LiquidIQPage;
